/**
 * Thymeleaf conversion utilities for template editor
 * Handles conversion from editor format to Thymeleaf server-side templates
 */

import {
  VARIABLE_PREFIX,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_TIME_FORMAT,
} from '../constants';
import { parseCondition } from '../conditions/parsing';

/**
 * Extract loop item names from th:each attributes in the HTML before a position
 * Returns array of [itemName, arrayPath] tuples in order from outermost to innermost loop
 */
const extractLoopContexts = (
  htmlBefore: string
): Array<{ itemName: string; arrayPath: string }> => {
  const contexts: Array<{ itemName: string; arrayPath: string }> = [];

  // Match th:each="itemName : ${path}" or th:each="itemName, iterStat : ${path}"
  // Handles both ${['@'].tests} and ${product.reviews} formats
  const thEachRegex =
    /th:each="([a-zA-Z_][\w]*)\s*(?:,\s*[a-zA-Z_][\w]*)?\s*:\s*\$\{[^}]+\}"/g;

  let match;
  while ((match = thEachRegex.exec(htmlBefore)) !== null) {
    const itemName = match[1];

    // Store just the item name - we only need to know what loop variable names are in scope
    contexts.push({
      itemName,
      arrayPath: '', // Not needed for our transformation logic
    });
  }

  return contexts;
};

/**
 * Check if a variable path refers to a loop item variable
 * Returns the transformed path if it does, null otherwise
 */
const transformLoopScopedPath = (
  variablePath: string,
  loopContexts: Array<{ itemName: string; arrayPath: string }>
): string | null => {
  // Iterate from innermost to outermost loop
  for (let i = loopContexts.length - 1; i >= 0; i--) {
    const { itemName } = loopContexts[i];

    // Check if variable path starts with itemName (e.g., "test.status" in loop with itemName "test")
    if (variablePath === itemName) {
      // Direct reference to loop item
      return itemName;
    }

    if (variablePath.startsWith(`${itemName}.`)) {
      // Reference to loop item property (e.g., "test.status")
      return variablePath;
    }
  }

  return null;
};

/**
 * Convert condition values to Thymeleaf expression
 * Now context-aware: detects loop-scoped variables and transforms them correctly
 */
const convertToThymeleafCondition = (
  variablePath: string,
  operator?: string,
  compareValue?: string | number | boolean | null,
  loopContexts: Array<{ itemName: string; arrayPath: string }> = []
): string => {
  // Check if this is a loop-scoped variable
  const loopScopedPath = transformLoopScopedPath(variablePath, loopContexts);

  // Use loop-scoped path if found, otherwise use global path with VARIABLE_PREFIX
  const thVarPath = loopScopedPath || `${VARIABLE_PREFIX}${variablePath}`;

  if (!operator) {
    return thVarPath;
  }

  let thValue: string;
  if (typeof compareValue === 'string') {
    thValue = `'${compareValue}'`;
  } else if (compareValue === null) {
    thValue = 'null';
  } else {
    thValue = String(compareValue);
  }

  switch (operator) {
    case '==':
      return `${thVarPath} == ${thValue}`;
    case '!=':
      return `${thVarPath} != ${thValue}`;
    case '>':
      return `${thVarPath} > ${thValue}`;
    case '>=':
      return `${thVarPath} >= ${thValue}`;
    case '<':
      return `${thVarPath} < ${thValue}`;
    case '<=':
      return `${thVarPath} <= ${thValue}`;
    case 'contains':
      return `${thVarPath} != null and ${thVarPath}.contains(${thValue})`;
    case '!contains':
      return `${thVarPath} == null or !${thVarPath}.contains(${thValue})`;
    case 'startsWith':
      return `${thVarPath} != null and ${thVarPath}.startsWith(${thValue})`;
    case 'endsWith':
      return `${thVarPath} != null and ${thVarPath}.endsWith(${thValue})`;
    case 'isEmpty':
      return `${thVarPath} == null or ${thVarPath}.isEmpty()`;
    case '!isEmpty':
      return `${thVarPath} != null and !${thVarPath}.isEmpty()`;
    default:
      return `${thVarPath} ${operator} ${thValue}`;
  }
};

/**
 * Check if we're currently inside an unclosed paragraph tag
 */
const checkIfInsideParagraph = (
  htmlBefore: string
): { isInside: boolean; depth: number } => {
  let depth = 0;

  const pOpenRegex = /<p(?:\s[^>]*)?>|<p>/g;
  const pCloseRegex = /<\/p>/g;

  let match;

  const openPositions: number[] = [];
  while ((match = pOpenRegex.exec(htmlBefore)) !== null) {
    openPositions.push(match.index);
  }

  const closePositions: number[] = [];
  while ((match = pCloseRegex.exec(htmlBefore)) !== null) {
    closePositions.push(match.index);
  }

  depth = openPositions.length - closePositions.length;

  return {
    isInside: depth > 0,
    depth: depth,
  };
};

/**
 * Remove all existing th:if and th:unless wrapper divs/spans from content
 */
const stripExistingThymeleafWrappers = (html: string): string => {
  let result = html;
  let changed = true;
  let iterations = 0;
  const maxIterations = 10;

  while (changed && iterations < maxIterations) {
    changed = false;
    iterations++;

    const newResult = result.replace(
      /<div\s+th:if="[^"]*">\s*([\s\S]*?)\s*<\/div>/g,
      (_, content) => {
        changed = true;
        return content;
      }
    );

    result = newResult.replace(
      /<span\s+th:if="[^"]*">\s*([\s\S]*?)\s*<\/span>/g,
      (_, content) => {
        changed = true;
        return content;
      }
    );

    result = result.replace(
      /<div\s+th:unless="[^"]*">\s*([\s\S]*?)\s*<\/div>/g,
      (_, content) => {
        changed = true;
        return content;
      }
    );
  }

  return result;
};

/**
 * Find the next condition marker starting from a position
 */
interface MarkerInfo {
  type: 'start' | 'else' | 'end';
  fullMatch: string;
  startMarker?: string;
  index: number;
}

const findNextMarker = (
  html: string,
  fromIndex: number,
  forceIndex: boolean = false
): MarkerInfo | null => {
  const startRegex =
    /<span[^>]*class="[^"]*template-condition-highlight[^"]*"[^>]*th:remove="all"[^>]*contenteditable="false">({{#(?:if|unless)\s+@\.[^}]+}})<\/span>/g;
  startRegex.lastIndex = fromIndex;
  const startMatch = startRegex.exec(html);

  const elseRegex =
    /<span[^>]*class="[^"]*template-condition-highlight[^"]*"[^>]*th:remove="all"[^>]*contenteditable="false">{{else}}<\/span>/g;
  elseRegex.lastIndex = forceIndex ? fromIndex : startMatch?.index || fromIndex;
  const elseMatch = elseRegex.exec(html);

  const endRegex =
    /<span[^>]*class="[^"]*template-condition-highlight[^"]*"[^>]*th:remove="all"[^>]*contenteditable="false">{{\/if}}<\/span>/g;
  endRegex.lastIndex = forceIndex
    ? fromIndex
    : elseMatch?.index || startMatch?.index || fromIndex;
  const endMatch = endRegex.exec(html);

  const candidates: MarkerInfo[] = [];

  if (startMatch) {
    candidates.push({
      type: 'start',
      fullMatch: startMatch[0],
      startMarker: startMatch[1],
      index: startMatch.index,
    });
  }

  if (elseMatch) {
    candidates.push({
      type: 'else',
      fullMatch: elseMatch[0],
      index: elseMatch.index,
    });
  }

  if (endMatch) {
    candidates.push({
      type: 'end',
      fullMatch: endMatch[0],
      index: endMatch.index,
    });
  }

  if (candidates.length === 0) {
    return null;
  }
  candidates.sort((a, b) => a.index - b.index);
  return candidates[0];
};

/**
 * Find matching end marker for a start marker using stack-based matching
 */
const findMatchingEnd = (
  html: string,
  startIndex: number
): { endMarker: MarkerInfo; elseMarker?: MarkerInfo } | null => {
  let depth = 1;
  let currentIndex = startIndex;
  let elseMarker: MarkerInfo | undefined;
  while (depth > 0) {
    const marker = findNextMarker(html, currentIndex, true);
    if (!marker) {
      return null;
    }

    if (marker.type === 'start') {
      depth++;
    } else if (marker.type === 'end') {
      depth--;
      if (depth === 0) {
        return { endMarker: marker, elseMarker };
      }
    } else if (marker.type === 'else' && depth === 1) {
      elseMarker = marker;
    }

    currentIndex = marker.index + marker.fullMatch.length;
  }

  return null;
};

/**
 * Process conditions recursively from innermost to outermost
 * @param html - HTML to process
 * @param accumulatedHtmlBefore - Accumulated HTML from previous processing (for context extraction)
 */
const processConditions = (
  html: string,
  accumulatedHtmlBefore: string = ''
): string => {
  const firstStart = findNextMarker(html, 0);

  if (!firstStart || firstStart.type !== 'start') {
    return html;
  }

  const match = findMatchingEnd(
    html,
    firstStart.index + firstStart.fullMatch.length
  );

  if (!match) {
    return html;
  }

  const { endMarker, elseMarker } = match;

  const ifContentStart = firstStart.index; //+ firstStart.fullMatch.length;
  const ifContentEnd = elseMarker ? elseMarker.index : endMarker.index;
  let ifContent = html.substring(ifContentStart, ifContentEnd);

  let elseContent: string | undefined;
  if (elseMarker) {
    const elseContentStart = elseMarker.index + elseMarker.fullMatch.length;
    const elseContentEnd = endMarker.index;
    elseContent = html.substring(elseContentStart, elseContentEnd);
  }

  // Recursively process nested conditions FIRST
  // Pass accumulated context including the HTML up to and including the start of the IF block
  const contextForIfContent =
    accumulatedHtmlBefore + html.substring(0, ifContentStart);
  ifContent = processConditions(ifContent, contextForIfContent);
  if (elseContent) {
    // For else content, context is the same as IF content
    elseContent = processConditions(elseContent, contextForIfContent);
  }

  const parsed = parseCondition(firstStart.startMarker!);
  if (!parsed) {
    const afterThis = html.substring(
      endMarker.index + endMarker.fullMatch.length
    );
    const contextForAfter =
      accumulatedHtmlBefore +
      html.substring(0, endMarker.index + endMarker.fullMatch.length);
    const processedAfter = processConditions(afterThis, contextForAfter);
    return (
      html.substring(0, endMarker.index + endMarker.fullMatch.length) +
      processedAfter
    );
  }

  // Extract loop contexts from accumulated HTML + current HTML before this condition
  const htmlBefore =
    accumulatedHtmlBefore + html.substring(0, firstStart.index);
  const loopContexts = extractLoopContexts(htmlBefore);

  const thConditionExpr = convertToThymeleafCondition(
    parsed.variablePath,
    parsed.operator,
    parsed.compareValue,
    loopContexts
  );

  const thAttribute =
    parsed.type === 'unless'
      ? `not (\${${thConditionExpr}})`
      : `\${${thConditionExpr}}`;

  const needsParagraphFix = checkIfInsideParagraph(htmlBefore);

  let wrapped = '';

  if (needsParagraphFix.isInside) {
    wrapped += '</span>\n';
  }

  wrapped += firstStart.fullMatch;
  wrapped += `\n<span th:if="${thAttribute}">\n`;

  if (needsParagraphFix.isInside) {
    wrapped += '<span>\n';
  }

  wrapped += ifContent;

  if (needsParagraphFix.isInside) {
    wrapped += '\n</span>';
  }

  wrapped += `\n</span>\n`;

  if (elseMarker && elseContent !== undefined) {
    const elseThAttribute =
      parsed.type === 'unless'
        ? `\${${thConditionExpr}}`
        : `not (\${${thConditionExpr}})`;
    wrapped += elseMarker.fullMatch;
    wrapped += `\n<span th:if="${elseThAttribute}">\n`;

    if (needsParagraphFix.isInside) {
      wrapped += '<span>\n';
    }

    wrapped += elseContent;

    if (needsParagraphFix.isInside) {
      wrapped += '\n</span>';
    }

    wrapped += `\n</span>\n`;
  }

  if (needsParagraphFix.isInside) {
    wrapped += '<span>\n';
  }

  wrapped += endMarker.fullMatch;

  const before = html.substring(0, firstStart.index);
  const after = html.substring(endMarker.index + endMarker.fullMatch.length);

  // Pass accumulated context including everything up to this point
  const contextForAfter = accumulatedHtmlBefore + before + wrapped;
  const processedAfter = processConditions(after, contextForAfter);

  return before + wrapped + processedAfter;
};

/**
 * Main entry point: strips existing wrappers once, then processes all conditions
 * Converts editor condition markers to Thymeleaf th:if attributes
 */
export const ensureThymeleafConditionWrappers = (html: string): string => {
  const stripped = stripExistingThymeleafWrappers(html);
  const result = processConditions(stripped);
  return result;
};

/**
 * Injects default date formats for formatDate and formatDateTime functions
 * that don't have a format argument. This ensures Thymeleaf validation passes.
 *
 * Transforms both:
 * - Display values: {{formatDate(@.path)}} → {{formatDate(@.path, 'dd.MM.yyyy')}}
 * - th:text attributes: ${#util.formatDate(['@'].path)} → ${#util.formatDate(['@'].path, 'dd.MM.yyyy')}
 *
 * Only transforms if format is not already present.
 * Works on both wrapped (in spans) and unwrapped patterns.
 *
 * @param html - HTML content with function markers
 * @returns HTML with default date formats injected where missing
 */
export const ensureDefaultDateFormats = (html: string): string => {
  let result = html;

  // Match formatDate without format in display value: {{formatDate(@.path)}} or {{formatDate(item.path)}}
  const formatDateDisplayRegex =
    /\{\{formatDate\(((?:@\.)?[a-zA-Z_][\w]*(?:\.[a-zA-Z_][\w]*)*)\)\}\}/g;

  result = result.replace(formatDateDisplayRegex, (match, variablePath) => {
    // Only transform if format is not already present
    if (!match.includes(",'")) {
      return `{{formatDate(${variablePath}, '${DEFAULT_DATE_FORMAT}')}}`;
    }
    return match;
  });

  // Match formatDate without format in th:text: ${#util.formatDate(['@'].path)} or ${#util.formatDate(path)}
  const formatDateThTextRegex =
    /th:text="\$\{#util\.formatDate\(((?:\[['"]@['"]\]\.)?[a-zA-Z_][\w]*(?:\.[a-zA-Z_][\w]*)*)\)\}"/g;

  result = result.replace(formatDateThTextRegex, (match, variablePath) => {
    // Only transform if format is not already present
    if (!match.includes(",'")) {
      return `th:text="\${#util.formatDate(${variablePath}, '${DEFAULT_DATE_FORMAT}')}"`;
    }
    return match;
  });

  // Match formatDateTime without format in display value: {{formatDateTime(@.path)}} or {{formatDateTime(item.path)}}
  const formatDateTimeDisplayRegex =
    /\{\{formatDateTime\(((?:@\.)?[a-zA-Z_][\w]*(?:\.[a-zA-Z_][\w]*)*)\)\}\}/g;

  result = result.replace(formatDateTimeDisplayRegex, (match, variablePath) => {
    // Only transform if format is not already present
    if (!match.includes(",'")) {
      return `{{formatDateTime(${variablePath}, '${DEFAULT_DATE_TIME_FORMAT}')}}`;
    }
    return match;
  });

  // Match formatDateTime without format in th:text: ${#util.formatDateTime(['@'].path)} or ${#util.formatDateTime(path)}
  const formatDateTimeThTextRegex =
    /th:text="\$\{#util\.formatDateTime\(((?:\[['"]@['"]\]\.)?[a-zA-Z_][\w]*(?:\.[a-zA-Z_][\w]*)*)\)\}"/g;

  result = result.replace(formatDateTimeThTextRegex, (match, variablePath) => {
    // Only transform if format is not already present
    if (!match.includes(",'")) {
      return `th:text="\${#util.formatDateTime(${variablePath}, '${DEFAULT_DATE_TIME_FORMAT}')}"`;
    }
    return match;
  });

  return result;
};
